/**
 * ConfirmationInfoBlocksEditor Component
 *
 * Structured editor for confirmation_info_blocks JSONB field.
 * Supports a two-level nested structure: blocks → items.
 * Each block has a title and a list of items (text + optional url).
 * Includes a Table/Raw toggle for easy copy-paste between locations.
 */

import { useState, useEffect } from 'react';
import { Plus, Trash2, AlertCircle, ChevronDown, ChevronUp, Link } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';

export interface InfoBlockItem {
  text: string;
  url?: string;
}

export interface InfoBlock {
  title: string;
  items: InfoBlockItem[];
}

interface ConfirmationInfoBlocksEditorProps {
  value: InfoBlock[] | null;
  onChange: (value: InfoBlock[] | null) => void;
  label: string;
}

type ViewMode = 'table' | 'raw';

export default function ConfirmationInfoBlocksEditor({
  value,
  onChange,
  label,
}: ConfirmationInfoBlocksEditorProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [rawJson, setRawJson] = useState('');
  const [jsonError, setJsonError] = useState('');
  const [collapsedBlocks, setCollapsedBlocks] = useState<Set<number>>(new Set());

  const blocks: InfoBlock[] = value || [];

  function handleSwitchToRaw() {
    setRawJson(JSON.stringify(blocks, null, 2));
    setJsonError('');
    setViewMode('raw');
  }

  function handleSwitchToTable() {
    if (rawJson.trim()) {
      try {
        const parsed = JSON.parse(rawJson);
        if (!Array.isArray(parsed)) {
          setJsonError(t('confirmationBlocks.mustBeArray'));
          return;
        }
        onChange(parsed);
      } catch (e) {
        setJsonError(t('confirmationBlocks.invalidJson'));
        return;
      }
    }
    setJsonError('');
    setViewMode('table');
  }

  function handleFormatValidate() {
    try {
      const parsed = JSON.parse(rawJson);
      setRawJson(JSON.stringify(parsed, null, 2));
      setJsonError('');
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : t('confirmationBlocks.invalidJson'));
    }
  }

  function handleRawApply() {
    try {
      const parsed = JSON.parse(rawJson);
      if (!Array.isArray(parsed)) {
        setJsonError(t('confirmationBlocks.mustBeArray'));
        return;
      }
      onChange(parsed);
      setJsonError('');
      setViewMode('table');
      showToast(t('confirmationBlocks.applied'), 'success');
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : t('confirmationBlocks.invalidJson'));
    }
  }

  // Block operations
  function addBlock() {
    onChange([...blocks, { title: '', items: [{ text: '' }] }]);
  }

  function removeBlock(blockIndex: number) {
    onChange(blocks.filter((_, i) => i !== blockIndex));
  }

  function updateBlockTitle(blockIndex: number, title: string) {
    const updated = blocks.map((block, i) =>
      i === blockIndex ? { ...block, title } : block
    );
    onChange(updated);
  }

  // Item operations
  function addItem(blockIndex: number) {
    const updated = blocks.map((block, i) =>
      i === blockIndex
        ? { ...block, items: [...block.items, { text: '' }] }
        : block
    );
    onChange(updated);
  }

  function removeItem(blockIndex: number, itemIndex: number) {
    const updated = blocks.map((block, i) =>
      i === blockIndex
        ? { ...block, items: block.items.filter((_, j) => j !== itemIndex) }
        : block
    );
    onChange(updated);
  }

  function updateItem(blockIndex: number, itemIndex: number, field: 'text' | 'url', value: string) {
    const updated = blocks.map((block, bi) =>
      bi === blockIndex
        ? {
            ...block,
            items: block.items.map((item, ii) =>
              ii === itemIndex
                ? { ...item, [field]: field === 'url' && !value ? undefined : value }
                : item
            ),
          }
        : block
    );
    onChange(updated);
  }

  function toggleCollapse(blockIndex: number) {
    setCollapsedBlocks(prev => {
      const next = new Set(prev);
      if (next.has(blockIndex)) {
        next.delete(blockIndex);
      } else {
        next.add(blockIndex);
      }
      return next;
    });
  }

  return (
    <div className="space-y-md">
      {/* Label and Toggle */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-primary">{label}</label>
        <div className="inline-flex rounded-lg border border-border bg-primary p-0.5">
          <button
            type="button"
            onClick={() => viewMode === 'raw' ? handleSwitchToTable() : null}
            className={`px-md py-xs text-sm font-medium rounded-md transition-colors ${
              viewMode === 'table'
                ? 'bg-blue text-white'
                : 'text-secondary hover:text-primary'
            }`}
          >
            {t('jsonbTable.simpleTableView')}
          </button>
          <button
            type="button"
            onClick={() => viewMode === 'table' ? handleSwitchToRaw() : null}
            className={`px-md py-xs text-sm font-medium rounded-md transition-colors ${
              viewMode === 'raw'
                ? 'bg-blue text-white'
                : 'text-secondary hover:text-primary'
            }`}
          >
            {t('jsonbTable.rawJsonView')}
          </button>
        </div>
      </div>

      {/* JSON Error */}
      {jsonError && (
        <div className="flex items-start gap-sm p-md bg-danger/10 border border-danger/20 rounded-lg">
          <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-danger">{t('jsonbTable.jsonError')}</p>
            <p className="text-sm text-danger/80 mt-xs font-mono">{jsonError}</p>
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="space-y-md">
          {blocks.length === 0 ? (
            <div className="text-center py-lg border border-border rounded-lg bg-secondary/10">
              <p className="text-sm text-secondary">{t('confirmationBlocks.noBlocks')}</p>
            </div>
          ) : (
            blocks.map((block, blockIndex) => {
              const isCollapsed = collapsedBlocks.has(blockIndex);
              return (
                <div
                  key={blockIndex}
                  className="border border-border rounded-lg overflow-hidden"
                >
                  {/* Block Header */}
                  <div className="flex items-center gap-sm p-md bg-secondary/5">
                    <button
                      type="button"
                      onClick={() => toggleCollapse(blockIndex)}
                      className="text-secondary hover:text-primary"
                    >
                      {isCollapsed ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronUp className="w-4 h-4" />
                      )}
                    </button>
                    <input
                      type="text"
                      value={block.title}
                      onChange={(e) => updateBlockTitle(blockIndex, e.target.value)}
                      className="input flex-1 text-sm font-medium"
                      placeholder={t('confirmationBlocks.blockTitlePlaceholder')}
                    />
                    <button
                      type="button"
                      onClick={() => removeBlock(blockIndex)}
                      className="btn-icon btn-sm text-danger"
                      title={t('confirmationBlocks.removeBlock')}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>

                  {/* Block Items */}
                  {!isCollapsed && (
                    <div className="p-md space-y-sm">
                      {block.items.map((item, itemIndex) => (
                        <div key={itemIndex} className="flex items-start gap-sm">
                          <span className="text-xs text-secondary mt-2.5 select-none">&bull;</span>
                          <div className="flex-1 space-y-xs">
                            <input
                              type="text"
                              value={item.text}
                              onChange={(e) => updateItem(blockIndex, itemIndex, 'text', e.target.value)}
                              className="input w-full text-sm"
                              placeholder={t('confirmationBlocks.itemTextPlaceholder')}
                            />
                            <div className="flex items-center gap-xs">
                              <Link className="w-3.5 h-3.5 text-secondary flex-shrink-0" />
                              <input
                                type="url"
                                value={item.url || ''}
                                onChange={(e) => updateItem(blockIndex, itemIndex, 'url', e.target.value)}
                                className="input w-full text-xs"
                                placeholder={t('confirmationBlocks.itemUrlPlaceholder')}
                              />
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeItem(blockIndex, itemIndex)}
                            className="btn-icon btn-sm text-danger mt-1"
                            title={t('confirmationBlocks.removeItem')}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        onClick={() => addItem(blockIndex)}
                        className="flex items-center gap-xs text-sm text-blue hover:text-blue/80 mt-sm"
                      >
                        <Plus className="w-3.5 h-3.5" />
                        {t('confirmationBlocks.addItem')}
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}

          <button
            type="button"
            onClick={addBlock}
            className="btn btn-secondary w-full text-sm"
          >
            <Plus className="w-4 h-4" />
            {t('confirmationBlocks.addBlock')}
          </button>
        </div>
      )}

      {/* Raw View */}
      {viewMode === 'raw' && (
        <div className="space-y-sm">
          <textarea
            value={rawJson}
            onChange={(e) => setRawJson(e.target.value)}
            className="input w-full font-mono text-xs"
            rows={16}
            placeholder="[]"
          />
          <div className="flex gap-sm">
            <button
              type="button"
              onClick={handleFormatValidate}
              className="btn btn-secondary text-sm"
            >
              {t('jsonbTable.formatValidate')}
            </button>
            <button
              type="button"
              onClick={handleRawApply}
              className="btn btn-primary text-sm"
            >
              {t('confirmationBlocks.applyToTable')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
